import type { BerthRotation } from "./types/voyages";
import type { AuditError, Schedule } from "./types/voyageSchedule";

interface Calls {
  berth_id: string;
  type: string;
  operations?: any[] | null;
  deliveries?: any[] | null;
  on_board_services?: any[] | null;
  bunkers?: any[] | null;
}

interface Job {
  job_id: string;
  vessel: string;
  status: string;
  assigned_agent: string | null;
  agent: {
    phone: string;
    email: string;
  } | null;
  voyage: string;
  berth_rotations: BerthRotation[];
  berths: {
    calls: Calls[];
  }[];
}

export const getBerthData = (job: Job) => {
  try {
    const berthRotations: BerthRotation[] = job.berth_rotations;
    const berthList = job.berths.reduce(
      (acc: Calls[], berths) => [...acc, ...berths.calls],
      []
    );
    
    // Helper to get call data (operations, deliveries, etc.) by berth_id
    const getCallData = (berthId: string) => {
      const call = berthList.find((c) => c.berth_id === berthId);
      return {
        operations: call?.operations || null,
        deliveries: call?.deliveries || null,
        on_board_services: call?.on_board_services || null,
        bunkers: call?.bunkers || null,
      };
    };
    const currentTime = new Date();

    // Find current berth without mutation
    const currentBerthInfo =
      berthRotations
        .map((berth, index) => {
          const eta = new Date(berth.eta);
          const etd = new Date(berth.etd);
          const shiftingStart = etd;
          const shiftingEnd = berth.shifting_hours
            ? new Date(etd.getTime() + berth.shifting_hours * 60 * 60 * 1000)
            : null;

          if (
            shiftingEnd &&
            currentTime >= shiftingStart &&
            currentTime < shiftingEnd
          ) {
            return {
              ...berth,
              status: "Shifting",
              index,
              type: null,
              isCurrentBerth: true,
            };
          }

          if (
            currentTime >= eta &&
            (currentTime < shiftingStart || !shiftingEnd)
          ) {
            return {
              ...berth,
              status: "At Berth",
              index,
              type:
                berthList.find((call) => call.berth_id === berth.berth_id)
                  ?.type || null,
              isCurrentBerth: true,
            };
          }

          return { ...berth, isCurrentBerth: false };
        })
        .find((b) => b.isCurrentBerth) || null;

    // Process current and next berth
    const currentBerth = currentBerthInfo
      ? (({ isCurrentBerth: _isCurrentBerth, ...rest }) => ({
          ...rest,
          ...getCallData(rest.berth_id),
        }))(currentBerthInfo)
      : null;

    let nextBerth: BerthRotation | null = null;
    if (currentBerth) {
      const nextIndex = currentBerth.index + 1;
      nextBerth = berthRotations[nextIndex]
        ? {
            ...berthRotations[nextIndex],
            ...getCallData(berthRotations[nextIndex].berth_id),
            type:
              berthList.find(
                (call) => call.berth_id === berthRotations[nextIndex].berth_id
              )?.type || null,
          }
        : null;
    } else {
      nextBerth = berthRotations[0]
        ? {
            ...berthRotations[0],
            ...getCallData(berthRotations[0].berth_id),
            type:
              berthList.find(
                (call) => call.berth_id === berthRotations[0].berth_id
              )?.type || null,
          }
        : null;
    }

    return {
      currentBerth,
      nextBerth,
    };
  } catch {
    return null;
  }
};

export const getCurrentBerthStatus = (berth: Schedule) => {
  try {
    const currentTime = new Date();
    const eta = new Date(berth.eta);
    const etd = new Date(berth.etd);
    const shiftingStart = etd;
    const shiftingEnd = berth.shifting
      ? new Date(etd.getTime() + berth.shifting * 60 * 60 * 1000)
      : null;
    if (
      shiftingEnd &&
      currentTime >= shiftingStart &&
      currentTime < shiftingEnd
    ) {
      return {
        status: "Shifting",
      };
    }
    if (currentTime >= eta && (currentTime < shiftingStart || !shiftingEnd)) {
      return {
        status: "At Berth",
      };
    }
    return {
      status: "Pending",
    };
  } catch {
    return {
      status: "Pending",
    };
  }
};

export const getCallsByBerthId = (voyageList: any, berthId: string) => {
  try {
    const voyage = voyageList.find((v: any) =>
      v.portsDetails.some((p: any) => p.berthId === berthId)
    );

    if (!voyage) return [];
    const port = voyage.portsDetails.find(
      (p: { berthId: string }) => p.berthId === berthId
    );
    return port?.calls || [];
  } catch {
    return [];
  }
};

export function auditBerths(berths: Schedule[]): Schedule[] {
  try {
    const warnings: string[] = [];
    const auditFunctions: {
      [key: string]: (pBerth: Schedule, cBerth: Schedule) => AuditError | false;
    } = {
      checkLocation: (
        pBerth: Schedule,
        cBerth: Schedule
      ): AuditError | false => {
        if (!cBerth.berth_location?.location_id) {
          return {
            errorScore: 999,
            errors:
              'Berth location cannot be empty. Select a berth the the "Stop" picklist.',
          };
        }
        if (
          pBerth.berth_location?.location_id ===
          cBerth.berth_location?.location_id
        ) {
          return {
            errors:
              "The previous location and current location are the same. Please check the values berths since this is not allowed.",
          };
        }
        return false;
      },
      checkShiftingHoursExist: (
        pBerth: Schedule,
        _cBerth: Schedule
      ): AuditError | false => {
        if (!pBerth.berth_location) return false;
        if (!pBerth.shifting || pBerth.shifting === 99) {
          return {
            errors: `Shifting hours between ${pBerth.berth_location?.name} and current location is not added due to possible missing entry in shifting matrix. 99 hours has been taken as default value until corrected.`,
          };
        }
        return false;
      },

      // Add more audit functions as needed
    };

    berths.forEach((berth, i) => {
      const errors: AuditError[] = [];
      if (i !== 0) {
        const currentLocation = berth;
        const prevLocation = berths[i - 1];
        Object.keys(auditFunctions).forEach((functionKey) => {
          const auditErrors = auditFunctions[functionKey](
            prevLocation,
            currentLocation
          );
          if (auditErrors) {
            errors.push(auditErrors);
          }
        });
        if (errors.length > 0) {
          currentLocation.isError = true;
          currentLocation.error_info = errors;
          currentLocation.warnings = warnings;
        }
      }
      if (i === 0 || i === berths.length - 1) {
        const berthLoc = berth.berth_location;
        if (!berthLoc) {
          errors.push({
            errorScore: 999,
            errors:
              'Berth location cannot be empty. Select a berth the the "Stop" picklist.',
          });
        } else if (berthLoc.type?.value !== "Anchorage") {
          errors.push({
            errors: `${i === 0 ? "First" : "Last"} Berth Should be Anchorage.`,
          });
        }
        if (errors.length > 0) {
          berth.isError = true;
          berth.error_info = errors;
        }
      }
    });

    return berths;
  } catch {
    return berths;
  }
}
